import React from 'react'

export interface GlowIntersectionOptions {
  /**
   * Determines whether the intersection check is active.
   */
  enabled?: boolean
  /**
   * Invoked when the glow begins intersecting the element.
   */
  onEnter?: () => void
}

interface GlowPointerPosition {
  x: number
  y: number
  timestamp: number
}

const GLOW_POINTER_EVENT = 'glow-pointer-position'

export const emitGlowPointerPosition = (position: GlowPointerPosition) => {
  window.dispatchEvent(new CustomEvent<GlowPointerPosition>(GLOW_POINTER_EVENT, { detail: position }))
}

export const useGlowIntersection = (
  ref: React.RefObject<HTMLElement | null>,
  { enabled = true, onEnter }: GlowIntersectionOptions = {}
) => {
  const hasEnteredRef = React.useRef(false)

  React.useEffect(() => {
    if (!enabled) return
    if (!ref.current) return

    const element = ref.current

    const handlePointer = (event: CustomEvent<GlowPointerPosition>) => {
      if (!element.isConnected) return

      const rect = element.getBoundingClientRect()
      const { x, y } = event.detail

      const isInside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom

      if (isInside && !hasEnteredRef.current) {
        hasEnteredRef.current = true
        onEnter?.()
      }
    }

    window.addEventListener(GLOW_POINTER_EVENT, handlePointer as EventListener)

    return () => {
      window.removeEventListener(GLOW_POINTER_EVENT, handlePointer as EventListener)
    }
  }, [enabled, onEnter, ref])
}
